import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Box, Button } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { schemaEmail } from "../scheme";
import { RegisterUserRequest } from "@/store/api/authApiData";
import { styles } from "../stylesSignUp";
import { useCheckUniqueMutation } from "@/store/api/authApi";
import CustomInput from "@/components/UI/CustomInput/CustomInput";
import HeaderControl from "./HeaderControl";
import TitleBlock from "./TitleBlock";
import BottomLink from "./BottomLink";

type FormInput = {
  email: string;
};

interface SignUpEmailProps {
  setSignUpStep: React.Dispatch<React.SetStateAction<number>>;
  setSignUpData: (data: Partial<RegisterUserRequest>) => void;
  signUpData: RegisterUserRequest;
}

const SignUpEmail: FC<SignUpEmailProps> = ({ setSignUpStep, setSignUpData, signUpData }) => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<FormInput>({
    resolver: yupResolver(schemaEmail),
    mode: "all",
  });

  const [checkUnique, { isLoading: isCheckingUnique }] = useCheckUniqueMutation();
  const [uniqEmail, setUniqEmail] = useState<boolean | undefined>(true);
  const [isSettled, setIsSettled] = useState(true);
  const emailValue = watch("email");
  useEffect(() => {
    setIsSettled(false);

    const timeout = setTimeout(async () => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!emailValue || !emailRegex.test(emailValue)) {
        setUniqEmail(true);
        setIsSettled(true);
        return;
      }

      try {
        const response = await checkUnique({ email: emailValue }).unwrap();
        setUniqEmail(response.email);
      } catch {
        setUniqEmail(true);
      } finally {
        setIsSettled(true);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [emailValue]);

  const onSubmit = async (data: FormInput) => {
    if (isCheckingUnique) {
      return;
    }
    try {
      const response = await checkUnique({ email: data.email }).unwrap();
      if (!response.email) {
        setUniqEmail(false);
        return;
      }
    } catch {
      // transient network error — don't block sign-up on it, same as the live-check behavior
    }
    setSignUpData({ ...signUpData, email: data.email });
    setSignUpStep(2);
  };

  return (
    <Box sx={styles.wrapper}>
      <HeaderControl onClick={() => setSignUpStep(0)} logo />

      <Box sx={{ marginTop: "32px" }}>
        <TitleBlock title={"Sign up"} subtitle="Create an account" />

        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{ width: "100%", marginTop: "44px" }}
        >
          <Box sx={{ marginBottom: "42px" }}>
            <CustomInput
              label="Email"
              placeholder="Enter your email"
              type="email"
              register={register}
              name="email"
              value={emailValue}
              error={!!errors.email || (isSettled && !uniqEmail && !isCheckingUnique && !!emailValue)}
              helperText={
                errors.email?.message
                  ? "Please enter a valid email address"
                  : isSettled && !uniqEmail && !isCheckingUnique && emailValue
                    ? "This email is already in use"
                    : ""
              }
            />
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={!isSettled || !isValid || !uniqEmail || isCheckingUnique}
            sx={styles.continueButton}
          >
            Continue
          </Button>

          <BottomLink sx={{ mt: 4 }} />
        </Box>
      </Box>
    </Box>
  );
};

export default SignUpEmail;
